/**
 * Movement patterns (#267): the pattern names, and the control that corrects
 * an exercise's pattern and main-lift flag in the builder.
 *
 * A FOURTH deck (IS-I10), rather than more entries in `firstRun.ts`,
 * `catalog.ts` or `coach.ts`: each scopes itself by its own docstring, and
 * this copy is none of first run, catalog setup or the coach's chrome.
 *
 * Its iOS twin is `ios/GroundworkKit/Sources/GroundworkKit/MovementCopy.swift`
 * (`enum MovementCopy`), and `python3 scripts/check_copy_decks.py` compares
 * the two on every symbol they share. **A string added here belongs there
 * too.** The iOS deck also carries the live workout's swap sheet, which the
 * web does not have (there is no live workout on the web), so those strings
 * are one-sided.
 *
 * Interface chrome, so English whatever the coach speaks. Placeholders are
 * `{name}`-style and substituted with `fill()` from `./firstRun`. No em
 * dashes (PRODUCT.md:47).
 */
export const MOVEMENT_COPY = {
  // ── Pattern names (IS-I1). Main lifts first, the order a picker lists them. ──
  patternHorizontalPress: 'Horizontal press',
  patternVerticalPress: 'Vertical press',
  patternHorizontalPull: 'Horizontal pull',
  patternVerticalPull: 'Vertical pull',
  patternSquat: 'Squat',
  patternHinge: 'Hinge',
  patternLunge: 'Lunge',
  patternKneeExtension: 'Knee extension',
  patternKneeFlexion: 'Knee flexion',
  patternHipAbduction: 'Hip abduction',
  patternHipAdduction: 'Hip adduction',
  patternHipExtension: 'Hip extension',
  patternElbowFlexion: 'Elbow flexion',
  patternElbowExtension: 'Elbow extension',
  patternShoulderRaise: 'Shoulder raise',
  patternChestFly: 'Chest fly',
  patternRearDeltFly: 'Rear delt fly',
  patternShrug: 'Shrug',
  patternCalfRaise: 'Calf raise',
  patternTrunkFlexion: 'Trunk flexion',
  patternTrunkExtension: 'Trunk extension',
  patternTrunkRotation: 'Trunk rotation',
  patternTrunkLateralFlexion: 'Side bend',
  patternTrunkStability: 'Trunk stability',
  // A pattern id this build does not know (the list is open, #182).
  patternUnknown: 'Other movement',

  // ── Examples under each name, so the list reads without anatomy ──
  hintHorizontalPress: 'Chest press, bench press, push-up',
  hintVerticalPress: 'Shoulder press, dips',
  hintHorizontalPull: 'Seated row, low row, bent-over row',
  hintVerticalPull: 'Lat pulldown, pull-up',
  hintSquat: 'Squat, leg press, hack squat',
  hintHinge: 'Deadlift, Romanian deadlift, kettlebell swing',
  hintLunge: 'Lunge, split squat, step-up',
  hintKneeExtension: 'Leg extension',
  hintKneeFlexion: 'Leg curl',
  hintHipAbduction: 'Abductor machine',
  hintHipAdduction: 'Adductor machine',
  hintHipExtension: 'Glute kickback, hip thrust',
  hintElbowFlexion: 'Biceps curl',
  hintElbowExtension: 'Triceps pushdown, skull crusher',
  hintShoulderRaise: 'Lateral raise, front raise, upright row',
  hintChestFly: 'Chest fly, pec deck',
  hintRearDeltFly: 'Reverse fly, face pull',
  hintShrug: 'Shrug',
  hintCalfRaise: 'Calf raise',
  hintTrunkFlexion: 'Crunch, sit-up, leg raise',
  hintTrunkExtension: 'Back extension',
  hintTrunkRotation: 'Rotary torso, cable woodchop',
  hintTrunkLateralFlexion: 'Side crunch, side bend',
  hintTrunkStability: 'Plank, anti-rotation hold',

  // ── The correction control (IS-I17), in the builder on both clients ──
  movementMenuItem: 'Movement pattern',
  movementTitle: 'Movement pattern',
  movementMainLifts: 'Main lifts',
  movementAccessories: 'Accessories',
  movementMainLiftToggle: 'Counts as a main lift',
  // A definition, not a promise: nothing here says what the flag changes,
  // because later slices decide that.
  movementMainLiftHelp: 'The big compound movements a plan is built around.',
  movementUseApp: "Use the app's answer",
  movementSourceYours: 'Set by you',
  movementSourceApp: "The app's answer",
  movementSourceNone: 'No movement pattern',
  movementSaveFailed: "Couldn't save that. Try again.",
  movementLoadFailed: "Couldn't load this exercise's movement.",
  // The web's alone: the dialog's close control. iOS closes a sheet by drag
  // and its own toolbar, so the iOS deck has no twin.
  movementClose: 'Close',

  // ── History (#267, IS-D6): a swap is a stand-in, not a change of plan ──
  // Shared with iOS, where the swap sheet's card and picker use it too.
  swapStandsInFor: 'Instead of {name}',
} as const

/** Every pattern id, in picker order, with its name and example line. */
export const PATTERN_LABELS: Record<string, { name: string; hint: string }> = {
  horizontal_press: { name: MOVEMENT_COPY.patternHorizontalPress, hint: MOVEMENT_COPY.hintHorizontalPress },
  vertical_press: { name: MOVEMENT_COPY.patternVerticalPress, hint: MOVEMENT_COPY.hintVerticalPress },
  horizontal_pull: { name: MOVEMENT_COPY.patternHorizontalPull, hint: MOVEMENT_COPY.hintHorizontalPull },
  vertical_pull: { name: MOVEMENT_COPY.patternVerticalPull, hint: MOVEMENT_COPY.hintVerticalPull },
  squat: { name: MOVEMENT_COPY.patternSquat, hint: MOVEMENT_COPY.hintSquat },
  hinge: { name: MOVEMENT_COPY.patternHinge, hint: MOVEMENT_COPY.hintHinge },
  lunge: { name: MOVEMENT_COPY.patternLunge, hint: MOVEMENT_COPY.hintLunge },
  knee_extension: { name: MOVEMENT_COPY.patternKneeExtension, hint: MOVEMENT_COPY.hintKneeExtension },
  knee_flexion: { name: MOVEMENT_COPY.patternKneeFlexion, hint: MOVEMENT_COPY.hintKneeFlexion },
  hip_abduction: { name: MOVEMENT_COPY.patternHipAbduction, hint: MOVEMENT_COPY.hintHipAbduction },
  hip_adduction: { name: MOVEMENT_COPY.patternHipAdduction, hint: MOVEMENT_COPY.hintHipAdduction },
  hip_extension: { name: MOVEMENT_COPY.patternHipExtension, hint: MOVEMENT_COPY.hintHipExtension },
  elbow_flexion: { name: MOVEMENT_COPY.patternElbowFlexion, hint: MOVEMENT_COPY.hintElbowFlexion },
  elbow_extension: { name: MOVEMENT_COPY.patternElbowExtension, hint: MOVEMENT_COPY.hintElbowExtension },
  shoulder_raise: { name: MOVEMENT_COPY.patternShoulderRaise, hint: MOVEMENT_COPY.hintShoulderRaise },
  chest_fly: { name: MOVEMENT_COPY.patternChestFly, hint: MOVEMENT_COPY.hintChestFly },
  rear_delt_fly: { name: MOVEMENT_COPY.patternRearDeltFly, hint: MOVEMENT_COPY.hintRearDeltFly },
  shrug: { name: MOVEMENT_COPY.patternShrug, hint: MOVEMENT_COPY.hintShrug },
  calf_raise: { name: MOVEMENT_COPY.patternCalfRaise, hint: MOVEMENT_COPY.hintCalfRaise },
  trunk_flexion: { name: MOVEMENT_COPY.patternTrunkFlexion, hint: MOVEMENT_COPY.hintTrunkFlexion },
  trunk_extension: { name: MOVEMENT_COPY.patternTrunkExtension, hint: MOVEMENT_COPY.hintTrunkExtension },
  trunk_rotation: { name: MOVEMENT_COPY.patternTrunkRotation, hint: MOVEMENT_COPY.hintTrunkRotation },
  trunk_lateral_flexion: {
    name: MOVEMENT_COPY.patternTrunkLateralFlexion,
    hint: MOVEMENT_COPY.hintTrunkLateralFlexion,
  },
  trunk_stability: { name: MOVEMENT_COPY.patternTrunkStability, hint: MOVEMENT_COPY.hintTrunkStability },
}

/** A pattern id's name; an id this build does not know reads as "Other movement". */
export function patternName(id: string | null | undefined): string {
  return (id && PATTERN_LABELS[id]?.name) || MOVEMENT_COPY.patternUnknown
}
